
class ImageLoadingManager {
    constructor(batchSize = 1, batchTimeout = 2000) {
        this.batchSize = batchSize;
        this.batchTimeout = batchTimeout; // milliseconds
        // Use a Map keyed by imageId. Each value is an object with a Set of listeners.
        this.queue = new Map();
        this.timeoutId = null;
        this.imagesDataChannel = null;
        // To store pending Promise resolvers keyed by imageId
        this.pendingRequests = {};
    }

    const
    ImageTypes = Object.freeze({
        SESSION_PREVIEW: 'session',
        RECORDING_PREVIEW: 'recording',
    });

    _getImageRequestUUID(imageId: number, imageType: string): string {
        return `${imageType}_${imageId}`;
    }

    // Call this to set the images data channel (for example, when the channel is open).
    setImagesDataChannel(channel) {
        this.imagesDataChannel = channel;

        this.imageId = null;
        this.mimeType = null;
        this.receiveBuffer = [];
        this.totalFileSize = 0;
        this.receivedBytes = 0;

        this.imagesDataChannel.onmessage = (event) => {
            console.log("onmessage:", event);
            // Check if event.data is a string (text message)
            if (typeof event.data === 'string') {
                try {
                    const msg = JSON.parse(event.data);
                    console.log("Received header message:", msg);

                    if (msg.command === 'error') {
                        const { imageId, error } = msg;
                        if (this.pendingRequests[imageId]) {
                            clearTimeout(this.pendingRequests[imageId].timeout);
                            this.pendingRequests[imageId].reject(new Error(error));
                            delete this.pendingRequests[imageId];
                        }
                    } else if (msg.command === 'image_data') {
                        // Save the header so we know which image this data belongs to
                        const { image_id, mime_type, size_bytes } = msg;
                        this.imageId = image_id;
                        this.mimeType = mime_type;
                        this.totalFileSize = size_bytes;
                        this.receiveBuffer = [];
                        this.receivedBytes = 0;
                    }
                } catch (error) {
                    console.error("Error parsing JSON message", error);
                }
            } else {
                console.log("Received arraybuffer message");
                // Otherwise, assume event.data is binary data (e.g., ArrayBuffer)
                if (this.imageId) {
                    this.receiveBuffer.push(event.data);
                    this.receivedBytes += event.data.byteLength;
                    console.log(`Received ${this.receivedBytes} of ${this.totalFileSize} bytes`);

                    if (this.receivedBytes === this.totalFileSize) {
                        console.log("All file data received!");
                        clearTimeout(this.pendingRequests[this.imageId].timeout);
                        const blob = new Blob(this.receiveBuffer, { type: this.mimeType || 'image/png' });
                        const url = URL.createObjectURL(blob);
                        this.pendingRequests[this.imageId].resolve(url);
                        delete this.pendingRequests[this.imageId];

                        // Clear the file loading data after handling the binary data.
                        this.imageId = null;
                        this.mimeType = null;
                        this.receiveBuffer = [];
                        this.totalFileSize = 0;
                        this.receivedBytes = 0;
                    }
                } else {
                    console.warn("Received binary data without a header");
                }
            }
        };
    }

    // Request an image load. listener must implement onSuccess(imageData) and onFailure(error)
    requestImage(imageId, imageType: string, listener) {
        console.log("Image loading request added", imageId, imageType);
        // need request ID since image IDs may be the same for images of the different types
        const requestID = this._getImageRequestUUID(imageId, imageType);
        if (this.queue.has(requestID)) {
            // If an entry exists, add the listener
            const targetRequest = this.queue.get(requestID)
            targetRequest.listeners.add(listener);
        } else {
            // Create a new entry for this imageId
            this.queue.set(requestID, {
                imageId: imageId,
                imageType: imageType,
                listeners: new Set([listener])
            });
        }

        // If the number of queued unique image requests reaches the batch size, process immediately.
        if (this.queue.size >= this.batchSize) {
            this._processQueue();
        } else if (!this.timeoutId) {
            // Otherwise, set a timeout to process the queue after a given delay.
            this.timeoutId = setTimeout(() => {
                this._processQueue();
            }, this.batchTimeout);
        }
    }

    // Cancel a request for a given imageId for a specific listener.
    cancelRequest(imageId, imageType, listener) {
        const requestID = this._getImageRequestUUID(imageId, imageType);
        if (this.queue.has(requestID)) {
            const entry = this.queue.get(requestID);
            entry.listeners.delete(listener);
            if (entry.listeners.size === 0) {
                this.queue.delete(requestID);
            }
        }
    }

    // Process the queued requests by loading all images in the queue.
    _processQueue() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        // Get all current queued requests and clear the queue so new ones can be added.
        const requests = Array.from(this.queue.entries());
        this.queue.clear();

        // For each imageId, load the image.
        requests.forEach(([requestID, { imageType, imageId, listeners }]) => {
            this._loadImage(imageType, imageId)
                .then((imageData) => {
                    listeners.forEach((listener) => listener.onSuccess(imageData));
                })
                .catch((error) => {
                    listeners.forEach((listener) => listener.onFailure(error));
                });
        });
    }

    // _loadImage now sends a command over the imagesDataChannel and waits for a response.
    _loadImage(imageType, imageId) {
        return new Promise((resolve, reject) => {
            if (!this.imagesDataChannel || this.imagesDataChannel.readyState !== 'open') {
                return reject(new Error("Images data channel is not open"));
            }

            // Store the promise handlers in pendingRequests.
            this.pendingRequests[imageId] = { resolve, reject };

            // Send the command to request the file.
            // The server is expected to receive a "get_file" command and then respond.
            const command = { command: 'get_file', img_type: imageType, img_id: imageId };
            this.imagesDataChannel.send(JSON.stringify(command));

            // Set a timeout to reject the promise if no response is received.
            const timeout = setTimeout(() => {
                if (this.pendingRequests[imageId]) {
                    this.pendingRequests[imageId].reject(new Error("Timeout waiting for image response"));
                    delete this.pendingRequests[imageId];
                }
            }, 5000); // Timeout after 5 seconds

            // Save the timeout handle.
            this.pendingRequests[imageId].timeout = timeout;
        });
    }
}

export default ImageLoadingManager;
